'use client';

import { FileType } from '@prisma/client';
import { imageExtensions } from '@schema/document.schema';
import { getFileIcon } from '@utils/browserFileHandling';
import { convertToSize, titlifyEnum } from '@utils/stringHelpers';
import { trpc } from '@utils/trpc';
import dynamic from 'next/dynamic';
import { useDebouncedValue } from '@mantine/hooks';
import { useCallback, useMemo, useState, type FC } from 'react';
import { ShortDate } from '~/components/date-times/ShortDate';
import FileLoadingIndicator from '~/components/form-fields/files/FileLoadingIndicator';
import { LoadingCircle } from '~/components/LoadingCircle';
import type { FormattedAttachment } from '~/components/pages/jobs/attachments';
import { Button } from '~/components/utils/buttons';
import { SearchInput } from '~/components/form-fields/native-html/SearchInput';
import { FAIcon } from '~/components/utils/FAIcons';
import { ModalDialog } from '~/components/utils/ModalDialog';
import { getNextPageParam, useInfinite } from '~/hooks/useInfinite';
import { CHAT_ATTACHABLE_FILE_TYPES } from '~/server/schema/chatDocuments';
import { getDocTypeColor } from '~/react_context/DocumentsContext';
import type { S3DocSelection } from './types';

const DynamicPdfThumbnail = dynamic(() => import('~/components/PDF/DynamicPdfThumbnail').then((mod) => mod.DynamicPdfThumbnail), { ssr: false });

// ─── Job search step ─────────────────────────────────────────────────────────

interface JobOption {
	label: string;
	sublabel?: string | null;
	value: number;
}

const JobSearchStep: FC<{
	onSelectJob: (job: JobOption) => void;
}> = ({ onSelectJob }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedQuery] = useDebouncedValue(searchQuery, 400);

	const { data: jobs, isLoading } = trpc.jobs.getJobOptionsForCombobox.useQuery(
		{ includeCompleted: true, searchQuery: debouncedQuery || undefined },
		{ keepPreviousData: true }
	);

	return (
		<div className="flex flex-col gap-3">
			<SearchInput fullWidth placeholder="Search jobs..." searchChange={(e) => setSearchQuery(e.target.value)} value={searchQuery} />

			<div className="max-h-[50vh] overflow-y-auto">
				{isLoading ? (
					<div className="flex justify-center py-8">
						<LoadingCircle size="text-3xl" />
					</div>
				) : !jobs?.length ? (
					<p className="py-8 text-center text-sm text-gray-400">No jobs found.</p>
				) : (
					<div className="flex flex-col gap-1">
						{jobs.map((job: JobOption) => (
							<button
								className="flex w-full flex-col rounded-md px-3 py-2 text-left transition-colors hover:bg-gray-100"
								key={job.value}
								onClick={() => onSelectJob(job)}
								type="button"
							>
								<span className="text-sm font-medium text-gray-900">{job.label}</span>
								{job.sublabel && <span className="text-xs text-gray-500">{job.sublabel}</span>}
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

// ─── Document selection step ─────────────────────────────────────────────────

const useAttachments = (jobId: number, fileTypeFilter: FileType[]) => {
	const {
		query: attachmentsQuery,
		data: attachments,
		isFetching,
		hasNextPage,
		fetchNextPage,
	} = useInfinite(
		trpc.documents.allPaged.useInfiniteQuery(
			{
				documentTypeFilter: [],
				fileTypeFilter,
				includePreviewLinks: true,
				jobId,
				limit: 20,
			},
			{ getNextPageParam }
		)
	);

	const formattedAttachments = attachments.map((attachmentData) => {
		const { fileType } = attachmentData;
		const fileTypeInfo = getFileIcon(fileType);

		return {
			allowedRoles: attachmentData.allowedRoles,
			date: attachmentData.createdAt,
			fileIcon: fileTypeInfo.icon,
			fileType,
			folderId: attachmentData.folderId,
			id: attachmentData.id,
			isCustomerSignatureRequired: attachmentData.isCustomerSignatureRequired,
			prettyType: fileTypeInfo.type,
			rawData: attachmentData,
			signedByCustomerAt: attachmentData.signedByCustomerAt,
			size: convertToSize(attachmentData.size),
			source: attachmentData.preSignedPreviewUrl,
			title: attachmentData.name,
			type: attachmentData.documentType,
			uploadedBy: attachmentData.uploadedByUser,
			viewedByCustomerAt: attachmentData.viewedByCustomerAt,
		} as FormattedAttachment;
	});

	return { attachments: formattedAttachments, attachmentsQuery, fetchNextPage, hasNextPage, isFetching };
};

const AttachmentCard: FC<{
	attachment: FormattedAttachment;
	isSelected: boolean;
	onToggle: (attachment: FormattedAttachment) => void;
}> = ({ attachment, isSelected, onToggle }) => {
	const isImage = imageExtensions.includes(attachment.fileType) && attachment.source;
	const isPdf = attachment.fileType === FileType.PDF && attachment.source;
	const showThumbnail = isImage || isPdf;
	const uploaderName = attachment.uploadedBy ? `${attachment.uploadedBy.firstName ?? ''} ${attachment.uploadedBy.lastName ?? ''}`.trim() : null;

	return (
		<div
			className={`relative flex cursor-pointer flex-col overflow-hidden rounded-lg border-2 transition-all ${
				isSelected ? 'border-glazier-blue-600 shadow-md' : 'border-gray-200 hover:border-glazier-blue-300 hover:shadow-sm'
			} bg-white`}
			onClick={() => onToggle(attachment)}
		>
			<div
				className={`relative flex h-36 items-center justify-center overflow-hidden ${showThumbnail ? 'bg-gray-100' : getDocTypeColor(attachment.type)}`}
			>
				{isImage ? (
					<img alt={attachment.title ?? 'Attachment'} className="h-full w-full object-cover" loading="lazy" src={attachment.source} />
				) : isPdf ? (
					<DynamicPdfThumbnail className="pointer-events-none" file={attachment.source} height={144} pageNumber={1} />
				) : (
					<FAIcon className="text-5xl opacity-70" icon={attachment.fileIcon ?? 'file'} />
				)}
				{isSelected && (
					<div className="absolute inset-0 flex items-start justify-end bg-glazier-blue-600/20 p-2">
						<div className="flex h-6 w-6 items-center justify-center rounded-full bg-glazier-blue-600 text-white shadow">
							<FAIcon className="text-xs" icon="check" />
						</div>
					</div>
				)}
				<div className="absolute bottom-2 left-2">
					<span className="rounded bg-black/50 px-1.5 py-0.5 text-xs font-semibold text-white">{attachment.fileType}</span>
				</div>
			</div>

			<div className="flex flex-1 flex-col gap-1 p-2.5">
				<p className="break-all text-sm font-medium leading-tight text-gray-900" title={attachment.title ?? undefined}>
					{attachment.title ?? 'Unnamed Document'}
				</p>
				<div className="mt-auto flex items-center justify-between gap-1 pt-1">
					<div className={`flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs ${getDocTypeColor(attachment.type)}`}>
						{titlifyEnum(attachment.type)}
					</div>
					<span className="shrink-0 text-xs text-gray-400">{attachment.size}</span>
				</div>
				<div className="flex items-center justify-between gap-1 text-xs text-gray-400">
					<span className="truncate">{uploaderName}</span>
					<ShortDate timestamp={attachment.rawData.createdAt} />
				</div>
			</div>
		</div>
	);
};

export interface JobDocumentPickerResult extends S3DocSelection {
	jobId: number;
	jobName: string;
	size: number;
}

const DocumentSelectionStep: FC<{
	fileTypeFilter: FileType[];
	jobId: number;
	jobName: string;
	onAttach: (docs: JobDocumentPickerResult[]) => void;
	onBack: () => void;
}> = ({ fileTypeFilter, jobId, jobName, onAttach, onBack }) => {
	const { attachments, attachmentsQuery, fetchNextPage, hasNextPage, isFetching } = useAttachments(jobId, fileTypeFilter);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

	const filteredAttachments = useMemo(
		() => (searchQuery.trim() ? attachments.filter((a) => a.title?.toLowerCase().includes(searchQuery.toLowerCase())) : attachments),
		[attachments, searchQuery]
	);

	const toggleAttachment = useCallback((attachment: FormattedAttachment) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(attachment.id)) {
				next.delete(attachment.id);
			} else {
				next.add(attachment.id);
			}
			return next;
		});
	}, []);

	const handleAttach = useCallback(() => {
		const selected = attachments.filter((a) => selectedIds.has(a.id));
		const docs: JobDocumentPickerResult[] = selected.map((a) => ({
			bucket: a.rawData.bucket,
			documentId: a.id,
			fileType: a.fileType,
			jobId,
			jobName,
			key: a.rawData.key,
			name: a.title ?? 'Unnamed',
			size: a.rawData.size ?? 0,
		}));
		onAttach(docs);
	}, [attachments, jobId, jobName, onAttach, selectedIds]);

	if (!attachmentsQuery?.isSuccess && attachmentsQuery?.isLoading) {
		return (
			<div className="flex flex-col items-center justify-center py-12">
				<FileLoadingIndicator />
			</div>
		);
	}

	if (attachmentsQuery?.isSuccess && attachments.length === 0) {
		return (
			<div className="flex flex-col items-center gap-4 py-8">
				<FAIcon className="text-4xl text-gray-300" icon="cloud-xmark" prefix="fal" />
				<p className="text-sm text-gray-500">No supported documents found for this job.</p>
				<Button className="text-sm" custom onClick={onBack}>
					<FAIcon className="mr-1" icon="arrow-left" prefix="fal" />
					Back to job search
				</Button>
			</div>
		);
	}

	return (
		<div className="flex max-h-[80vh] flex-col">
			{/* Header with back button */}
			<div className="mb-3 flex items-center gap-2">
				<button
					className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
					onClick={onBack}
					type="button"
				>
					<FAIcon icon="arrow-left" prefix="fal" />
					Back
				</button>
				<span className="text-sm font-medium text-gray-700">{jobName}</span>
			</div>

			{/* Search */}
			<div className="mb-3">
				<SearchInput fullWidth placeholder="Search files..." searchChange={(e) => setSearchQuery(e.target.value)} value={searchQuery} />
			</div>

			{/* Grid */}
			<div className="flex-1 overflow-y-auto pr-1">
				{filteredAttachments.length === 0 && !isFetching ? (
					<p className="py-8 text-center text-sm text-gray-400">No files match your search.</p>
				) : (
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
						{filteredAttachments.map((attachment) => (
							<AttachmentCard
								attachment={attachment}
								isSelected={selectedIds.has(attachment.id)}
								key={attachment.id}
								onToggle={toggleAttachment}
							/>
						))}
					</div>
				)}
				{isFetching && (
					<div className="py-4">
						<LoadingCircle size="text-3xl" />
					</div>
				)}
				{hasNextPage && !isFetching && (
					<div className="mt-3 flex justify-center">
						<Button
							className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
							custom
							onClick={fetchNextPage}
						>
							<FAIcon className="mr-2" icon="arrow-down" prefix="fal" />
							Load More
						</Button>
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
				<span className="text-sm text-gray-500">{selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Click files to select'}</span>
				<Button
					className="bg-glazier-blue-600 text-white hover:bg-glazier-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200"
					disabled={selectedIds.size === 0}
					onClick={handleAttach}
				>
					<FAIcon className="mr-2" icon="arrow-up-from-bracket" prefix="fal" />
					Attach {selectedIds.size > 0 && `(${selectedIds.size})`}
				</Button>
			</div>
		</div>
	);
};

// ─── Main dialog ─────────────────────────────────────────────────────────────

interface JobDocumentPickerProps {
	defaultJobId?: number;
	defaultJobName?: string;
	fileTypeFilter?: FileType[];
	isOpen: boolean;
	onAttach?: (docs: JobDocumentPickerResult[]) => void;
	onClose: () => void;
}

const JobDocumentPicker: FC<JobDocumentPickerProps> = ({
	defaultJobId,
	defaultJobName,
	fileTypeFilter = CHAT_ATTACHABLE_FILE_TYPES,
	isOpen,
	onAttach,
	onClose,
}) => {
	const [selectedJob, setSelectedJob] = useState<JobOption | null>(
		defaultJobId && defaultJobName ? { label: defaultJobName, value: defaultJobId } : null
	);

	const handleClose = useCallback(() => {
		setSelectedJob(null);
		onClose();
	}, [onClose]);

	const handleAttach = useCallback(
		(docs: JobDocumentPickerResult[]) => {
			onAttach?.(docs);
			handleClose();
		},
		[handleClose, onAttach]
	);

	return (
		<ModalDialog closeFn={handleClose} dialogOpen={isOpen} panelContainerClass="md:max-w-4xl" zIndex="z-[10001]">
			<h2 className="mb-4 text-lg font-semibold text-gray-900">{selectedJob ? 'Select Documents' : 'Select a Job'}</h2>
			{selectedJob ? (
				<DocumentSelectionStep
					fileTypeFilter={fileTypeFilter}
					jobId={selectedJob.value}
					jobName={selectedJob.label}
					onAttach={handleAttach}
					onBack={() => setSelectedJob(null)}
				/>
			) : (
				<JobSearchStep onSelectJob={setSelectedJob} />
			)}
		</ModalDialog>
	);
};

export default JobDocumentPicker;
